<template>
  <div v-if="isVisible" class="modal-overlay">
    <div class="modal-content">
      <!-- Modal Header -->
      <div class="modal-header">
        <h3>Agregar Nueva Suscripción</h3>
        <button type="button" class="btn-close" @click="$emit('close')">&times;</button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Select Plan -->
        <div class="form-group">
          <label for="plan">Plan <span class="required">*</span></label>
          <select 
            id="plan" 
            v-model="formData.plan_id" 
            @change="onPlanChange"
            class="form-control"
            :class="{ 'error': errors.plan_id }"
          >
            <option value="">Selecciona un plan</option>
            <option v-for="plan in plans" :key="plan.id" :value="plan.id">
              {{ plan.name }}
            </option>
          </select>
          <span v-if="errors.plan_id" class="error-message">{{ errors.plan_id }}</span>
        </div>

        <!-- Precio (auto-llenado) -->
        <div class="form-group">
          <label for="price">Precio <span class="required">*</span></label>
          <input 
            type="text" 
            id="price" 
            v-model="formattedPrice" 
            class="form-control"
            disabled
          >
        </div>

        <!-- Tipo de Pago -->
        <div class="form-group">
          <label for="payment_type">Tipo de Pago <span class="required">*</span></label>
          <select 
            id="payment_type" 
            v-model="formData.payment_type"
            class="form-control"
            :class="{ 'error': errors.payment_type }"
          >
            <option value="">Selecciona tipo de pago</option>
            <option value="subscription">Suscripción</option>
            <option value="transfer">Transferencia</option>
          </select>
          <span v-if="errors.payment_type" class="error-message">{{ errors.payment_type }}</span>
        </div>

        <!-- Ciclo de Facturación -->
        <div class="form-group">
          <label for="billing_cycle">Ciclo de Facturación <span class="required">*</span></label>
          <select 
            id="billing_cycle" 
            v-model="formData.billing_cycle"
            class="form-control"
            :class="{ 'error': errors.billing_cycle }"
          >
            <option value="">Selecciona ciclo</option>
            <option value="monthly">Mensual</option>
            <option value="yearly">Anual</option>
          </select>
          <span v-if="errors.billing_cycle" class="error-message">{{ errors.billing_cycle }}</span>
        </div>

        <!-- Fecha de Inicio -->
        <div class="form-group">
          <label for="start_date">Fecha de Inicio <span class="required">*</span></label>
          <input 
            type="date" 
            id="start_date" 
            v-model="formData.starts_at"
            class="form-control"
            :class="{ 'error': errors.starts_at }"
          >
          <span v-if="errors.starts_at" class="error-message">{{ errors.starts_at }}</span>
        </div>

        <!-- Fecha de Fin -->
        <div class="form-group">
          <label for="end_date">Fecha de Fin <span class="required">*</span></label>
          <input 
            type="date" 
            id="end_date" 
            v-model="formData.ends_at"
            class="form-control"
            :class="{ 'error': errors.ends_at }"
          >
          <span v-if="errors.ends_at" class="error-message">{{ errors.ends_at }}</span>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-cancel" :disabled="isLoading">
          Cancelar
        </button>
        <button @click="handleSubmit" class="btn btn-confirm" :disabled="isLoading">
          {{ isLoading ? 'Guardando...' : 'Confirmar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  },
  advisorId: {
    type: [String, Number],
    required: true
  },
  plans: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'submit'])

const isLoading = ref(false)
const formData = ref({
  plan_id: '',
  amount: 0,
  payment_type: '',
  billing_cycle: '',
  starts_at: '',
  ends_at: ''
})

const errors = ref({})

// Precio formateado
const formattedPrice = computed(() => {
  if (!formData.value.amount) return '$0.00'
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(formData.value.amount)
})

// Cuando cambia el plan seleccionado
const onPlanChange = () => {
  const selectedPlan = props.plans.find(p => p.id === formData.value.plan_id)
  if (selectedPlan) {
    formData.value.amount = selectedPlan.price || 0
  } else {
    formData.value.amount = 0
  }
  // Limpiar error de plan
  if (errors.value.plan_id) {
    delete errors.value.plan_id
  }
}

// Validación
const validate = () => {
  errors.value = {}
  
  if (!formData.value.plan_id) {
    errors.value.plan_id = 'Selecciona un plan'
  }
  
  if (!formData.value.payment_type) {
    errors.value.payment_type = 'Selecciona tipo de pago'
  }
  
  if (!formData.value.billing_cycle) {
    errors.value.billing_cycle = 'Selecciona ciclo de facturación'
  }
  
  if (!formData.value.starts_at) {
    errors.value.starts_at = 'Ingresa fecha de inicio'
  }
  
  if (!formData.value.ends_at) {
    errors.value.ends_at = 'Ingresa fecha de fin'
  }
  
  // Validar que la fecha de fin sea posterior a la de inicio
  if (formData.value.starts_at && formData.value.ends_at) {
    if (new Date(formData.value.ends_at) <= new Date(formData.value.starts_at)) {
      errors.value.ends_at = 'La fecha de fin debe ser posterior a la de inicio'
    }
  }
  
  return Object.keys(errors.value).length === 0
}

const handleSubmit = () => {
  if (!validate()) return
  
  emit('submit', {
    ...formData.value,
    advisor_id: props.advisorId
  })
}

// Resetear formulario cuando se cierra el modal
watch(() => props.isVisible, (newVal) => {
  if (!newVal) {
    formData.value = {
      plan_id: '',
      amount: 0,
      payment_type: '',
      billing_cycle: '',
      starts_at: '',
      ends_at: ''
    }
    errors.value = {}
    isLoading.value = false
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required {
  color: #ef4444;
}

.form-control {
  width: 100%;
  padding: 0.625rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #FF4265;
  box-shadow: 0 0 0 3px rgba(255, 66, 101, 0.1);
}

.form-control:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.form-control.error {
  border-color: #ef4444;
}

.error-message {
  display: block;
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #ef4444;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #737373;
  color: white;
}

.btn-cancel:hover:not(:disabled) {
  background: #525252;
}

.btn-confirm {
  background: #FF4265;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #e63859;
}
</style>